import {useLocalization} from '@/hooks/useLocalization';
import {useRouter} from '@/i18n/routing';
import {SearchProduct} from '@winelivery-org/wl-client-sdk-js/dist/models/search';
import {useFormatter, useTranslations} from 'next-intl';
import dynamic from 'next/dynamic';
import Image from 'next/image';
import {Button} from '../../../../../components/design-system';
import {Hearth as IconHearth, Plus} from '../../../../../components/icons';
import logger from '../../../../../logger';
import NoUserError from '../errors/NoUserError';
import useProduct from '../hooks/useProduct';
import AddressModal from './AddressModal/AddressModal';
import useAddressSearch from './AddressModal/hook';

const QuantityManager = dynamic(() => import('./QuantityManager'), {
  ssr: false,
});

interface Props {
  product: SearchProduct;
  onClick?: (product: SearchProduct) => void;
}

export default function ProductItem({product, onClick = () => null}: Props) {
  const t = useTranslations('common');
  const format = useFormatter();
  const router = useRouter();
  const {location, deliveryType} = useLocalization();

  const {
    quantityInCart,
    isInWishList,
    addToCart,
    addToWishList,
    removeFromCart,
    removeFromWishList,
  } = useProduct(product);
  const {updateGlobalLocation} = useAddressSearch('');

  function handleWishListClick() {
    try {
      if (isInWishList) {
        removeFromWishList();
      } else {
        addToWishList();
      }
    } catch (e) {
      if (e instanceof NoUserError) {
        router.push({
          pathname: `/shop/[id]`,
          params: {id: product.slug!},
        });
      }
      logger.error(e);
    }
  }

  const renderQuantityManager = () => {
    if (!product.inStock) {
      return (
        <div
          className="text-md font-bold leading-5 truncate"
          style={{color: '#cf1c44'}}
        >
          {t('Availability.NotAvailable')}
        </div>
      );
    }

    if (product.deliveryType === 'ecommerce' && deliveryType !== 'ecommerce') {
      return (
        <div
          className="text-md font-bold leading-5 text-right"
          style={{color: '#43d9a3'}}
        >
          {t('Availability.AvailableInShipping')}
        </div>
      );
    }

    return (
      <QuantityManager
        quantity={quantityInCart}
        onAdd={addToCart}
        onSub={removeFromCart}
      />
    );
  };

  return (
    <div
      className={`
        flex-row py-6 sm:flex-col group cursor-pointer flex gap-3 h-full
      `}
      onClick={() => {
        if (onClick) {
          onClick(product);
        }
      }}
    >
      <div
        className={`
        xs:border sm:border-none xs:border-greyscale-separator md:border-transparent relative bg-white rounded overflow-hidden shrink-0 py-2
      `}
      >
        <div className="relative sm:h-52 sm:w-full w-32 h-32">
          <Image
            fill
            className="object-contain transition ease-in-out duration-500 group-hover:scale-125"
            src={product.imageUrl || ''}
            alt={product.name}
          />
        </div>
        <button
          className="absolute top-2 right-2 flex items-center justify-center h-6 w-6 bg-white border border-greyscale-separator rounded-lg text-secondary-dark hover:shadow-sm"
          onClick={(e) => {
            e.preventDefault();
            handleWishListClick();
          }}
        >
          <IconHearth
            className={`h-4 ${
              isInWishList ? 'fill-secondary-light' : ''
            }`}
          />
        </button>
      </div>
      <div className="flex-1 flex flex-col justify-between">
        <div className="space-y-0.5">
          <h3 className="text-base font-bold tracking-tight text-greyscale-body m-0 leading-5 group-hover:underline">
            {product.name}
          </h3>
          <p className="text-sm text-greyscale-label font-medium tracking-tight">
            {product.producer}
          </p>
          <p className="text-sm text-greyscale-placeholder font-medium tracking-tight">
            {product.country ? product.country.name : ''}
            {product.region ? `, ${product.region.name}` : ''}
          </p>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex flex-col text-base tracking-tight m-0 gap-0 leading-5">
            {!!product.discountedPrice && (
              <span className="text-sm line-through text-greyscale-label font-medium">
                {format.number(product.price, {
                  style: 'currency',
                  currency: 'EUR',
                })}
              </span>
            )}
            <span className="text-greyscale-body font-bold">
              {format.number(product.discountedPrice || product.price, {
                style: 'currency',
                currency: 'EUR',
              })}
            </span>
          </div>
          {!location?.coordinates?.lat && !location?.coordinates?.lng ? (
            <AddressModal
              showSavedAddresses
              showLastAddress
              onChangeAddress={updateGlobalLocation}
              renderTrigger={({open}) => (
                <Button
                  rounded
                  theme="outline"
                  variant="icon"
                  onClick={(e) => {
                    e.preventDefault();
                    open();
                  }}
                >
                  <Plus />
                </Button>
              )}
            />
          ) : (
            renderQuantityManager()
          )}
        </div>
      </div>
    </div>
  );
}
